import { Check, ChevronDown, Home, LoaderCircle, PenLine, Plus, Trash2, Upload, X } from "lucide-react";
import { FormEvent, PointerEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  addPractice,
  deleteAccount,
  getAccountProfile,
  getPracticeOptions,
  removePractice,
  removeSignature,
  searchPracticeAddresses,
  setPrimaryPractice,
  updateAccountProfile,
  updateSignature,
  type AccountProfile,
  type PracticeAddress,
  type PracticeOption,
  type Practice
} from "../lib/portalApi";
import { appConfig } from "../config/appConfig";
import { isValidUsZipCode, normalizeUsZipCode } from "../lib/usZipCode";

type SaveState = "idle" | "saving" | "saved" | "error";
type PracticeFormState = {
  selectedPracticeId: string;
  manualName: string;
  addressSearch: string;
  selectedAddress: PracticeAddress | null;
  address: PracticeAddress;
};

const manualPracticeValue = "__manual__";
const initialPracticeForm: PracticeFormState = {
  selectedPracticeId: "",
  manualName: "",
  addressSearch: "",
  selectedAddress: null,
  address: {
    label: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: ""
  }
};

export function AccountPage({
  onAccountDeleted,
  onProfileChange
}: {
  onAccountDeleted?: () => void;
  onProfileChange?: (profile: AccountProfile) => void;
}) {
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [licenseSaveState, setLicenseSaveState] = useState<SaveState>("idle");
  const [practiceForm, setPracticeForm] = useState<PracticeFormState>(initialPracticeForm);
  const [practiceOptions, setPracticeOptions] = useState<PracticeOption[]>([]);
  const [practiceOptionsError, setPracticeOptionsError] = useState<string | null>(null);
  const [addressSuggestions, setAddressSuggestions] = useState<PracticeAddress[]>([]);
  const [isAddressSearching, setIsAddressSearching] = useState(false);
  const [isPracticeFormOpen, setIsPracticeFormOpen] = useState(false);
  const [pendingPracticeIds, setPendingPracticeIds] = useState<Set<string>>(() => new Set());
  const [pendingPracticeKeys, setPendingPracticeKeys] = useState<Set<string>>(() => new Set());
  const [activePracticeAction, setActivePracticeAction] = useState<{
    id: string;
    type: "delete" | "primary";
  } | null>(null);
  const [practiceActionError, setPracticeActionError] = useState<string | null>(null);
  const [practiceSaveState, setPracticeSaveState] = useState<SaveState>("idle");
  const [signatureSaveState, setSignatureSaveState] = useState<SaveState>("idle");
  const [signatureErrorMessage, setSignatureErrorMessage] = useState<string | null>(null);
  const [testDeaState, setTestDeaState] = useState<SaveState>("idle");
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingSignatureRef = useRef(false);

  function phoneDigits(value: string): string {
    return value.replace(/\D/g, "").slice(0, 10);
  }

  function formatPhone(value: string): string {
    const digits = phoneDigits(value);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  function normalizeDeaInput(value: string): string {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 9);
  }

  function cleanAddressLine2(value: string | undefined): string {
    const cleaned = (value ?? "").trim();
    return /^suite\s*\/\s*apt\s*\/\s*unit$/i.test(cleaned) ? "" : cleaned;
  }

  function apiErrorCode(error: unknown): string {
    const payload = (error as { payload?: unknown } | null)?.payload;
    if (payload && typeof payload === "object" && "error" in payload) {
      return String((payload as { error?: unknown }).error ?? "");
    }
    return "";
  }

  function signatureSaveMessage(error: unknown): string {
    const code = apiErrorCode(error);
    if (code === "signature_too_small_or_blank") {
      return "Please draw a slightly larger signature before saving.";
    }
    if (code === "signature_must_be_png" || code === "signature_invalid_png" || code === "signature_invalid_base64") {
      return "Signature image could not be read. Please clear the pad and draw it again.";
    }
    if (code === "signature_too_large" || code === "request_body_too_large") {
      return "Signature image is too large. Please draw it again or upload a smaller image.";
    }
    if (error instanceof Error && error.message === "signature_not_persisted") {
      return "Signature was received but could not be loaded back to your account. Please try again.";
    }
    return "Signature could not be saved. Please try again.";
  }

  function canSaveProfile(account: AccountProfile): boolean {
    return Boolean(account.firstName.trim() && account.lastName.trim() && phoneDigits(account.phone).length === 10);
  }

  function practiceIdentityKey(name: string, address: string) {
    return `${name.toLowerCase().replace(/\s+/g, " ").trim()}|${address.toLowerCase().replace(/\s+/g, " ").trim()}`;
  }

  function practiceAddressKey(address: PracticeAddress) {
    return [address.line1, cleanAddressLine2(address.line2), address.city, address.state, address.postalCode]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(", ");
  }

  function applyPendingPracticeState(
    nextProfile: AccountProfile,
    pendingIds = pendingPracticeIds,
    pendingKeys = pendingPracticeKeys
  ): AccountProfile {
    if (pendingIds.size === 0 && pendingKeys.size === 0) return nextProfile;
    return {
      ...nextProfile,
      practices: nextProfile.practices.map((practice) =>
        practice.status !== "active" &&
        !practice.isPrimary &&
        (pendingIds.has(practice.id) || pendingKeys.has(practiceIdentityKey(practice.name, practice.address)))
          ? { ...practice, isPrimary: false, status: "pending" }
          : practice
      )
    };
  }

  function commitProfile(nextProfile: AccountProfile, pendingIds = pendingPracticeIds, pendingKeys = pendingPracticeKeys) {
    const hydratedProfile = applyPendingPracticeState(nextProfile, pendingIds, pendingKeys);
    setProfile(hydratedProfile);
    onProfileChange?.(hydratedProfile);
  }

  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);
    getAccountProfile()
      .then(commitProfile)
      .catch(() => {
        setProfile(null);
        setLoadError("We could not load your account details right now.");
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    setSignaturePreview(profile?.signaturePreviewUrl ?? null);
  }, [profile?.signaturePreviewUrl]);

  useEffect(() => {
    if (!isPracticeFormOpen) return;
    setPracticeOptionsError(null);
    getPracticeOptions()
      .then(setPracticeOptions)
      .catch(() => setPracticeOptionsError("Practice options could not be loaded. You can still choose manual entry."));
  }, [isPracticeFormOpen]);

  useEffect(() => {
    if (practiceForm.selectedPracticeId !== manualPracticeValue || practiceForm.addressSearch.trim().length < 3) {
      setAddressSuggestions([]);
      setIsAddressSearching(false);
      return;
    }

    let isCurrent = true;
    setIsAddressSearching(true);
    const timeoutId = window.setTimeout(() => {
      searchPracticeAddresses(practiceForm.addressSearch.trim())
        .then((suggestions) => {
          if (isCurrent) setAddressSuggestions(suggestions);
        })
        .catch(() => {
          if (isCurrent) setAddressSuggestions([]);
        })
        .finally(() => {
          if (isCurrent) setIsAddressSearching(false);
        });
    }, 250);

    return () => {
      isCurrent = false;
      window.clearTimeout(timeoutId);
    };
  }, [practiceForm.addressSearch, practiceForm.selectedPracticeId]);

  useEffect(() => {
    if (saveState !== "saved") return;
    const timeoutId = window.setTimeout(() => setSaveState("idle"), 6000);
    return () => window.clearTimeout(timeoutId);
  }, [saveState]);

  async function handleProfileSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!profile) return;
    if (!canSaveProfile(profile)) {
      setSaveState("error");
      return;
    }

    setSaveState("saving");
    try {
      const nextProfile = await updateAccountProfile({
        firstName: profile.firstName.trim(),
        lastName: profile.lastName.trim(),
        phone: formatPhone(profile.phone)
      });
      commitProfile(nextProfile);
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }

  function updateField<K extends keyof AccountProfile>(key: K, value: AccountProfile[K]) {
    setProfile((current) => (current ? { ...current, [key]: value } : current));
  }

  async function handlePracticeSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const selectedPracticeId = practiceForm.selectedPracticeId;
    if (!selectedPracticeId) return;
    if (
      selectedPracticeId === manualPracticeValue &&
      (!practiceForm.manualName.trim() || !practiceForm.address.line1.trim())
    ) {
      return;
    }

    setPracticeSaveState("saving");
    if (selectedPracticeId === manualPracticeValue && !isValidUsZipCode(practiceForm.address.postalCode)) {
      setPracticeSaveState("error");
      setPracticeActionError("Enter a valid practice ZIP code.");
      return;
    }
    setPracticeActionError(null);

    try {
      const currentPracticeIds = new Set((profile?.practices ?? []).map((practice) => practice.id));
      let pendingPracticeKey = "";
      let nextProfile: AccountProfile;

      if (selectedPracticeId === manualPracticeValue) {
        const name = practiceForm.manualName.trim();
        pendingPracticeKey = practiceIdentityKey(name, practiceAddressKey(practiceForm.address));

        nextProfile = await addPractice({
          mode: "manual",
          name,
          address: {
            ...practiceForm.address,
            line2: cleanAddressLine2(practiceForm.address.line2),
            label: practiceAddressKey(practiceForm.address)
          }
        });
      } else {
        const selectedPractice = practiceOptions.find((practice) => practice.id === selectedPracticeId);
        pendingPracticeKey = practiceIdentityKey(selectedPractice?.name ?? "", selectedPractice?.address ?? "");
        nextProfile = await addPractice({ mode: "existing", practiceId: selectedPracticeId });
      }

      const addedPracticeIds = nextProfile.practices
        .filter((practice) => !currentPracticeIds.has(practice.id))
        .map((practice) => practice.id);
      const nextPendingIds = new Set(pendingPracticeIds);
      addedPracticeIds.forEach((id) => nextPendingIds.add(id));
      const nextPendingKeys = pendingPracticeKey ? new Set(pendingPracticeKeys).add(pendingPracticeKey) : pendingPracticeKeys;
      setPendingPracticeIds(nextPendingIds);
      setPendingPracticeKeys(nextPendingKeys);
      commitProfile(nextProfile, nextPendingIds, nextPendingKeys);

      setPracticeSaveState("saved");
      window.setTimeout(() => setPracticeSaveState("idle"), 1600);
      setPracticeForm(initialPracticeForm);
      setIsPracticeFormOpen(false);
    } catch {
      setPracticeSaveState("error");
      setPracticeActionError("Practice could not be added. Please try again.");
    }
  }

  async function makePrimary(practiceId: string) {
    setActivePracticeAction({ id: practiceId, type: "primary" });
    setPracticeActionError(null);
    try {
      commitProfile(await setPrimaryPractice(practiceId));
    } catch {
      setPracticeActionError("Practice could not be updated. Please try again.");
    } finally {
      setActivePracticeAction(null);
    }
  }

  async function deletePractice(practiceId: string) {
    setActivePracticeAction({ id: practiceId, type: "delete" });
    setPracticeActionError(null);
    try {
      const deletedPractice = profile?.practices.find((practice) => practice.id === practiceId);
      const nextPendingIds = new Set(pendingPracticeIds);
      nextPendingIds.delete(practiceId);
      const nextPendingKeys = new Set(pendingPracticeKeys);
      if (deletedPractice) {
        nextPendingKeys.delete(practiceIdentityKey(deletedPractice.name, deletedPractice.address));
      }
      setPendingPracticeIds(nextPendingIds);
      setPendingPracticeKeys(nextPendingKeys);
      commitProfile(await removePractice(practiceId), nextPendingIds, nextPendingKeys);
    } catch {
      setPracticeActionError("Practice could not be removed. Please try again.");
    } finally {
      setActivePracticeAction(null);
    }
  }

  async function handleSignatureUpload(file: File | null) {
    if (!file) return;
    setSignatureSaveState("saving");
    setSignatureErrorMessage(null);
    try {
      const signatureBase64 = await fileToPngBase64(file);
      const nextProfile = await updateSignature(signatureBase64);
      if (!nextProfile.hasSignature || !nextProfile.signaturePreviewUrl) {
        throw new Error("signature_not_persisted");
      }
      commitProfile(nextProfile);
      setSignaturePreview(nextProfile.signaturePreviewUrl);
      clearSignaturePad();
      setSignatureSaveState("saved");
      window.setTimeout(() => setSignatureSaveState("idle"), 1800);
    } catch (error) {
      setSignatureErrorMessage(signatureSaveMessage(error));
      setSignatureSaveState("error");
    }
  }

  async function handleSignatureRemove() {
    setSignatureSaveState("saving");
    setSignatureErrorMessage(null);
    try {
      commitProfile(await removeSignature());
      setSignaturePreview(null);
      clearSignaturePad();
      setSignatureSaveState("saved");
      window.setTimeout(() => setSignatureSaveState("idle"), 1800);
    } catch (error) {
      setSignatureErrorMessage(signatureSaveMessage(error));
      setSignatureSaveState("error");
    }
  }

  function prepareSignatureCanvas() {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scale = window.devicePixelRatio || 1;
    const width = Math.max(Math.floor(rect.width * scale), 1);
    const height = Math.max(Math.floor(rect.height * scale), 1);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    const context = canvas.getContext("2d");
    if (!context) return null;
    context.setTransform(scale, 0, 0, scale, 0, 0);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = 2.4;
    context.strokeStyle = "#16253f";
    return { canvas, context, rect };
  }

  function signaturePoint(event: PointerEvent<HTMLCanvasElement>) {
    const prepared = prepareSignatureCanvas();
    if (!prepared) return null;
    return {
      context: prepared.context,
      x: event.clientX - prepared.rect.left,
      y: event.clientY - prepared.rect.top
    };
  }

  function startSignature(event: PointerEvent<HTMLCanvasElement>) {
    const point = signaturePoint(event);
    if (!point) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    isDrawingSignatureRef.current = true;
    point.context.beginPath();
    point.context.moveTo(point.x, point.y);
  }

  function drawSignature(event: PointerEvent<HTMLCanvasElement>) {
    if (!isDrawingSignatureRef.current) return;
    const point = signaturePoint(event);
    if (!point) return;
    point.context.lineTo(point.x, point.y);
    point.context.stroke();
    setHasDrawnSignature(true);
  }

  function stopSignature(event: PointerEvent<HTMLCanvasElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    isDrawingSignatureRef.current = false;
  }

  function clearSignaturePad() {
    const canvas = signatureCanvasRef.current;
    const context = canvas?.getContext("2d");
    if (canvas && context) {
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      prepareSignatureCanvas();
    }
    isDrawingSignatureRef.current = false;
    setHasDrawnSignature(false);
  }

  async function saveDrawnSignature() {
    const canvas = signatureCanvasRef.current;
    if (!canvas || !hasDrawnSignature) return;
    setSignatureSaveState("saving");
    setSignatureErrorMessage(null);
    try {
      const signatureBase64 = canvas.toDataURL("image/png");
      const nextProfile = await updateSignature(signatureBase64);
      if (!nextProfile.hasSignature || !nextProfile.signaturePreviewUrl) {
        throw new Error("signature_not_persisted");
      }
      commitProfile(nextProfile);
      setSignaturePreview(nextProfile.signaturePreviewUrl);
      clearSignaturePad();
      setSignatureSaveState("saved");
      window.setTimeout(() => setSignatureSaveState("idle"), 1800);
    } catch (error) {
      setSignatureErrorMessage(signatureSaveMessage(error));
      setSignatureSaveState("error");
    }
  }

  async function handleAccountDelete() {
    if (deleteConfirm !== "DELETE" || isDeletingAccount) return;
    setIsDeletingAccount(true);
    setDeleteAccountError(null);
    try {
      await deleteAccount({ confirmation: "DELETE" });
      setIsDeleteModalOpen(false);
      onAccountDeleted?.();
    } catch (error) {
      const code = apiErrorCode(error);
      setDeleteAccountError(
        code === "account_not_found"
          ? "This account is already unavailable. Please refresh and sign in again if needed."
          : "Account deletion could not be completed. Please try again or contact support."
      );
    } finally {
      setIsDeletingAccount(false);
    }
  }

  async function addTestDeaValue() {
    if (import.meta.env.VITE_APP_ENV !== "test" || appConfig.environment !== "test" || !generateTestDeaNumber || !profile) return;
    setTestDeaState("saving");
    try {
      const deaNumber = generateTestDeaNumber();
      const nextProfile = await updateAccountProfile({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone,
        deaNumber
      });
      commitProfile(nextProfile);
      setTestDeaState("saved");
      window.setTimeout(() => setTestDeaState("idle"), 1600);
    } catch {
      setTestDeaState("error");
    }
  }

  async function handleLicenseSave() {
    if (!profile) return;
    setLicenseSaveState("saving");
    try {
      const nextProfile = await updateAccountProfile({
        deaNumber: normalizeDeaInput(profile.deaNumber)
      });
      commitProfile(nextProfile);
      setLicenseSaveState("saved");
      window.setTimeout(() => setLicenseSaveState("idle"), 1800);
    } catch {
      setLicenseSaveState("error");
    }
  }

  async function handleDeaClear() {
    if (!profile || !profile.deaNumber || licenseSaveState === "saving") return;
    setLicenseSaveState("saving");
    setTestDeaState("idle");
    try {
      const nextProfile = await updateAccountProfile({ deaNumber: "" });
      commitProfile(nextProfile);
      setLicenseSaveState("saved");
      window.setTimeout(() => setLicenseSaveState("idle"), 1800);
    } catch {
      setLicenseSaveState("error");
    }
  }

  if (isLoading) {
    return (
      <div className="settings-grid">
        <section className="settings-section skeleton-block" aria-label="Loading account settings" />
        <section className="settings-section skeleton-block" aria-label="Loading practices" />
      </div>
    );
  }

  if (loadError || !profile) {
    return (
      <section className="settings-section account-error" aria-labelledby="account-error-heading">
        <p className="eyebrow">Account</p>
        <h2 id="account-error-heading">Account details unavailable</h2>
        <p>{loadError ?? "We could not load your account details right now."}</p>
        <p className="field-note account-error-note">
          Please refresh or try again. If this continues, contact{" "}
          <a href={`mailto:${appConfig.support.email}`}>{appConfig.support.email}</a>.
        </p>
        <button
          className="secondary-button compact"
          type="button"
          onClick={() => {
            setIsLoading(true);
            setLoadError(null);
            getAccountProfile()
              .then(commitProfile)
              .catch(() => setLoadError("We could not load your account details right now."))
              .finally(() => setIsLoading(false));
          }}
        >
          Try again
        </button>
      </section>
    );
  }

  return (
    <div className="account-layout">
      <section className="settings-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Profile</p>
            <h2>Personal information</h2>
          </div>
          {saveState === "saved" ? (
            <div className="profile-save-toast" role="status" aria-live="polite" aria-atomic="true">
              <Check size={20} aria-hidden="true" />
              <span>Profile changes saved.</span>
              <button type="button" aria-label="Dismiss save confirmation" onClick={() => setSaveState("idle")}>
                <span aria-hidden="true">×</span>
              </button>
            </div>
          ) : null}
        </div>

        <form className="field-grid" onSubmit={handleProfileSubmit}>
          <label>
            First name
            <input value={profile.firstName} onChange={(event) => updateField("firstName", event.target.value)} />
          </label>
          <label>
            Last name
            <input value={profile.lastName} onChange={(event) => updateField("lastName", event.target.value)} />
          </label>
          <label>
            Phone
            <input
              type="tel"
              value={profile.phone}
              inputMode="numeric"
              placeholder="(555) 555-5555"
              onChange={(event) => updateField("phone", formatPhone(event.target.value))}
            />
          </label>
          <label className="span-2">
            Email
            <input value={profile.email} disabled />
          </label>
          <p className="field-note centered-note span-2">
            To update your email, contact{" "}
            <a href={`mailto:${appConfig.support.email}`}>{appConfig.support.email}</a>.
          </p>
          <button className="primary-button align-start" type="submit" disabled={saveState === "saving"}>
            {saveState === "saving" ? "Saving..." : "Save changes"}
          </button>
          {saveState === "error" ? (
            <p className="error-text span-2">
              Enter first name, last name, and a 10-digit phone number before saving.
            </p>
          ) : null}
        </form>
      </section>

      <section className="settings-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">License</p>
            <h2>License information</h2>
          </div>
        </div>
        <div className="field-grid">
          <label>
            Veterinary medical board license number
            <input value={profile.licenseNumber ?? ""} disabled />
          </label>
          <label>
            State of licensure
            <input value={profile.licenseState ?? ""} disabled />
          </label>
          <label className="dea-field span-2" htmlFor="account-dea-number">
            DEA number
            <span className="inline-input-row">
              <span className="clearable-input">
                <input
                  id="account-dea-number"
                  value={profile.deaNumber}
                  inputMode="text"
                  maxLength={9}
                  onChange={(event) => updateField("deaNumber", normalizeDeaInput(event.target.value))}
                />
                <button
                  className="clear-input-button"
                  type="button"
                  aria-label="Clear DEA number"
                  title="Clear DEA number"
                  disabled={!profile.deaNumber || licenseSaveState === "saving"}
                  onClick={() => void handleDeaClear()}
                >
                  <X size={16} aria-hidden="true" />
                </button>
              </span>
              {import.meta.env.VITE_APP_ENV === "test" && appConfig.environment === "test" ? (
                <button
                  className="inline-link-button"
                  type="button"
                  disabled={testDeaState === "saving"}
                  onClick={addTestDeaValue}
                >
                  {testDeaState === "saving" ? "Generating..." : "Add test DEA value"}
                </button>
              ) : null}
            </span>
          </label>
          {licenseSaveState === "saved" ? (
            <p className="form-success span-2">DEA number saved.</p>
          ) : null}
          {import.meta.env.VITE_APP_ENV === "test" && testDeaState === "saved" ? (
            <p className="form-success span-2">A valid test DEA value was generated and saved.</p>
          ) : null}
          {import.meta.env.VITE_APP_ENV === "test" && testDeaState === "error" ? (
            <p className="field-note warning span-2">Test DEA value could not be generated. Please try again.</p>
          ) : null}
          <p className="field-note centered-note span-2">
            To update your license information, contact{" "}
            <a href={`mailto:${appConfig.support.email}`}>{appConfig.support.email}</a> or{" "}
            <a href={`tel:${appConfig.support.phoneDigits}`}>{appConfig.support.phone}</a>.
          </p>
          <button className="primary-button align-start" type="button" disabled={licenseSaveState === "saving"} onClick={handleLicenseSave}>
            {licenseSaveState === "saving" ? "Saving..." : "Save changes"}
          </button>
          {licenseSaveState === "error" ? (
            <p className="form-alert span-2">DEA number is not valid. Enter a properly formatted DEA number and try again.</p>
          ) : null}
        </div>
      </section>

      <section className="settings-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Practices</p>
            <h2>Practice locations</h2>
          </div>
          <button className="secondary-button compact" type="button" onClick={() => setIsPracticeFormOpen((open) => !open)}>
            <Plus size={16} />
            Add practice
          </button>
        </div>
        {isPracticeFormOpen ? (
          <form className="inline-form" autoComplete="off" data-lpignore="true" data-1p-ignore="true" onSubmit={handlePracticeSubmit}>
            <label>
              Practice name
              <SearchableSelect
                options={[
                  { value: manualPracticeValue, label: "--- My practice is not listed ---", searchText: "my practice is not listed" },
                  ...practiceOptions.map((practice) => ({
                    value: practice.id,
                    label: practice.label,
                    searchText: `${practice.name} ${practice.address} ${practice.label}`
                  }))
                ]}
                placeholder="Search for a practice..."
                value={practiceForm.selectedPracticeId}
                valueLabel={
                  practiceForm.selectedPracticeId === manualPracticeValue
                    ? "--- My practice is not listed ---"
                    : practiceOptions.find((practice) => practice.id === practiceForm.selectedPracticeId)?.label ?? ""
                }
                onChange={(selectedPracticeId) =>
                  setPracticeForm({
                    ...initialPracticeForm,
                    selectedPracticeId
                  })
                }
              />
            </label>
            {practiceOptionsError ? <p className="field-note warning">{practiceOptionsError}</p> : null}
            {practiceForm.selectedPracticeId === manualPracticeValue ? (
              <>
                <label>
                  New practice name
                  <input
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    value={practiceForm.manualName}
                    onChange={(event) => setPracticeForm((current) => ({ ...current, manualName: event.target.value }))}
                    required
                  />
                </label>
                <label className="address-search-field">
                  Practice address
                  <input
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    value={practiceForm.addressSearch}
                    onChange={(event) =>
                      setPracticeForm((current) => ({
                        ...current,
                        addressSearch: event.target.value,
                        selectedAddress: null
                      }))
                    }
                    placeholder="Start typing an address..."
                    required
                  />
                </label>
                <div className="address-suggestions" aria-live="polite">
                  {isAddressSearching ? <p className="field-note">Searching addresses...</p> : null}
                  {!isAddressSearching && practiceForm.addressSearch.trim().length >= 3 && addressSuggestions.length === 0 ? (
                    <p className="field-note">No matching addresses found.</p>
                  ) : null}
                  {addressSuggestions.map((address) => (
                    <button
                      className={practiceForm.selectedAddress?.id === address.id ? "address-suggestion selected" : "address-suggestion"}
                      type="button"
                      key={address.id ?? address.label}
                      onClick={() =>
                        setPracticeForm((current) => ({
                          ...current,
                          addressSearch: address.label,
                          selectedAddress: address,
                          address: {
                            ...address,
                            postalCode: normalizeUsZipCode(address.postalCode),
                            line2: cleanAddressLine2(address.line2)
                          }
                        }))
                      }
                    >
                      {address.label}
                    </button>
                  ))}
                </div>
                <div className="practice-address-grid">
                  <label>
                    Address line one
                    <input
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      value={practiceForm.address.line1}
                      onChange={(event) =>
                        setPracticeForm((current) => ({
                          ...current,
                          address: { ...current.address, line1: event.target.value }
                        }))
                      }
                      required
                    />
                  </label>
                  <label>
                    Suite / Apt / Unit
                    <input
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      value={practiceForm.address.line2 ?? ""}
                      onChange={(event) =>
                        setPracticeForm((current) => ({
                          ...current,
                          address: { ...current.address, line2: cleanAddressLine2(event.target.value) }
                        }))
                      }
                    />
                  </label>
                  <label>
                    City
                    <input
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      value={practiceForm.address.city}
                      onChange={(event) =>
                        setPracticeForm((current) => ({
                          ...current,
                          address: { ...current.address, city: event.target.value }
                        }))
                      }
                      required
                    />
                  </label>
                  <label>
                    State
                    <input
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      value={practiceForm.address.state}
                      onChange={(event) =>
                        setPracticeForm((current) => ({
                          ...current,
                          address: { ...current.address, state: event.target.value.toUpperCase().slice(0, 2) }
                        }))
                      }
                      required
                    />
                  </label>
                  <label>
                    Zip
                    <input
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      inputMode="numeric"
                      value={practiceForm.address.postalCode}
                      maxLength={5}
                      pattern="[0-9]{5}"
                      onPaste={(event) => {
                        event.preventDefault();
                        const input = event.currentTarget;
                        const value = input.value.slice(0, input.selectionStart ?? 0) + event.clipboardData.getData("text") + input.value.slice(input.selectionEnd ?? input.value.length);
                        setPracticeForm((current) => ({ ...current, address: { ...current.address, postalCode: normalizeUsZipCode(value) } }));
                      }}
                      onChange={(event) =>
                        setPracticeForm((current) => ({
                          ...current,
                          address: { ...current.address, postalCode: normalizeUsZipCode(event.target.value) }
                        }))
                      }
                      required
                    />
                  </label>
                </div>
              </>
            ) : null}
            <div className="button-row">
              <button
                className="primary-button compact"
                type="submit"
                disabled={
                  practiceSaveState === "saving" ||
                  !practiceForm.selectedPracticeId ||
                  (practiceForm.selectedPracticeId === manualPracticeValue &&
                    (!practiceForm.manualName.trim() || !practiceForm.address.line1.trim()))
                }
              >
                {practiceSaveState === "saving" ? "Saving practice..." : "Save practice"}
              </button>
              <button
                className="secondary-button compact"
                type="button"
                disabled={practiceSaveState === "saving"}
                onClick={() => {
                  setPracticeForm(initialPracticeForm);
                  setIsPracticeFormOpen(false);
                  setPracticeSaveState("idle");
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}
        {practiceActionError ? <p className="form-alert">{practiceActionError}</p> : null}
        <div className="practice-list">
          {profile.practices.map((practice) => (
            <PracticeRow
              key={practice.id}
              practice={practice}
              busyAction={activePracticeAction?.id === practice.id ? activePracticeAction.type : null}
              isActionDisabled={Boolean(activePracticeAction)}
              onDelete={() => deletePractice(practice.id)}
              onMakePrimary={() => makePrimary(practice.id)}
            />
          ))}
        </div>
      </section>

      <section className="settings-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Signature</p>
            <h2>Prescriber signature</h2>
          </div>
        </div>
        <div className="signature-panel">
          <div className="signature-surface">
            <p className="signature-surface-label">Saved signature</p>
            <div className="signature-preview">
              {signaturePreview ? (
                <img src={signaturePreview} alt="Saved prescriber signature preview" />
              ) : profile.hasSignature ? (
                <span className="signature-placeholder">
                  <PenLine size={28} aria-hidden="true" />
                  Signature preview is loading.
                </span>
              ) : (
                <span className="signature-placeholder">No signature is saved on file.</span>
              )}
            </div>
          </div>
          <div className="signature-surface">
            <p className="signature-surface-label">Draw signature</p>
            <div className="signature-pad-shell">
              {!hasDrawnSignature ? <span className="signature-pad-placeholder">Draw signature here</span> : null}
              <canvas
                ref={signatureCanvasRef}
                className="signature-pad"
                aria-label="Draw prescriber signature"
                onPointerDown={startSignature}
                onPointerMove={drawSignature}
                onPointerUp={stopSignature}
                onPointerCancel={stopSignature}
                onPointerLeave={stopSignature}
              />
            </div>
          </div>
          <div className="signature-actions">
            <div className="signature-action-group">
              <button
                className="primary-button"
                type="button"
                disabled={!hasDrawnSignature || signatureSaveState === "saving"}
                onClick={saveDrawnSignature}
              >
                {signatureSaveState === "saving" ? "Saving..." : "Save drawn signature"}
              </button>
              <button className="secondary-button" type="button" disabled={!hasDrawnSignature} onClick={clearSignaturePad}>
                Clear pad
              </button>
            </div>
            <div className="signature-action-group">
              <label className="secondary-button signature-upload">
                <Upload size={16} />
                Upload Signature
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(event) => {
                    void handleSignatureUpload(event.target.files?.[0] ?? null);
                    event.currentTarget.value = "";
                  }}
                />
              </label>
              {profile.hasSignature ? (
                <button
                  className="danger-button"
                  type="button"
                  disabled={signatureSaveState === "saving"}
                  onClick={handleSignatureRemove}
                >
                  <Trash2 size={16} />
                  Remove
                </button>
              ) : null}
            </div>
          </div>
          {signatureSaveState === "saved" ? (
            <span className="status-pill success">
              <Check size={14} />
              Signature saved
            </span>
          ) : null}
          {signatureSaveState === "error" ? (
            <p className="error-text">{signatureErrorMessage ?? "Signature could not be saved. Please try again."}</p>
          ) : null}
        </div>
      </section>

      <section className="settings-section danger-zone">
        <div className="section-header">
          <div>
            <p className="eyebrow">Security</p>
            <h2>Delete account</h2>
          </div>
        </div>
        <p>This action opens a confirmation step before your portal access is removed.</p>
        <button
          className="danger-button"
          type="button"
          onClick={() => {
            setDeleteConfirm("");
            setDeleteAccountError(null);
            setIsDeleteModalOpen(true);
          }}
        >
          Delete account
        </button>
      </section>
      {isDeleteModalOpen ? (
        <div className="modal-backdrop" role="presentation">
          <section className="modal" role="dialog" aria-modal="true" aria-labelledby="delete-account-title">
            <div className="modal-header">
              <div>
                <p className="eyebrow">Confirm Delete</p>
                <h2 id="delete-account-title">Delete account</h2>
              </div>
              <button
                className="icon-button"
                type="button"
                aria-label="Close delete confirmation"
                disabled={isDeletingAccount}
                onClick={() => setIsDeleteModalOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
            <p>
              This will deactivate portal access for this prescriber account. Existing prescription records remain
              governed by Companion Pharmacy record retention policies.
            </p>
            <label>
              Type DELETE to confirm
              <input
                autoFocus
                value={deleteConfirm}
                disabled={isDeletingAccount}
                onChange={(event) => {
                  setDeleteConfirm(event.target.value);
                  setDeleteAccountError(null);
                }}
              />
            </label>
            {deleteAccountError ? <p className="form-alert" role="alert">{deleteAccountError}</p> : null}
            <div className="button-row modal-actions">
              <button
                className="secondary-button"
                type="button"
                disabled={isDeletingAccount}
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="danger-button"
                type="button"
                disabled={deleteConfirm !== "DELETE" || isDeletingAccount}
                onClick={handleAccountDelete}
              >
                {isDeletingAccount ? "Deleting..." : "Delete account"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function imageToPngBase64(source: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const sourceWidth = image.naturalWidth || image.width;
      const sourceHeight = image.naturalHeight || image.height;
      if (!sourceWidth || !sourceHeight) {
        reject(new Error("Image could not be processed"));
        return;
      }
      const maxDimension = 640;
      const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight));
      const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
      const targetHeight = Math.max(1, Math.round(sourceHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Image could not be processed"));
        return;
      }
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, targetWidth, targetHeight);
      resolve(canvas.toDataURL("image/png"));
    };
    image.onerror = () => reject(new Error("Image could not be loaded"));
    image.src = source;
  });
}

async function fileToPngBase64(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) throw new Error("Signature upload must be an image");
  return imageToPngBase64(await fileToBase64(file));
}

const generateTestDeaNumber = import.meta.env.VITE_APP_ENV === "test" ? function generateTestDeaNumber(): string {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const second = letters[Math.floor(Math.random() * letters.length)];
  const firstSixDigits = Array.from({ length: 6 }, () => Math.floor(Math.random() * 10));
  const weightedSum =
    firstSixDigits[0] +
    firstSixDigits[2] +
    firstSixDigits[4] +
    2 * (firstSixDigits[1] + firstSixDigits[3] + firstSixDigits[5]);
  const checkDigit = weightedSum % 10;

  return `F${second}${firstSixDigits.join("")}${checkDigit}`;
} : undefined;

function PracticeRow({
  practice,
  busyAction,
  isActionDisabled,
  onDelete,
  onMakePrimary
}: {
  practice: Practice;
  busyAction: "delete" | "primary" | null;
  isActionDisabled: boolean;
  onDelete: () => void;
  onMakePrimary: () => void;
}) {
  const isPending = practice.status === "pending";
  const isNotApproved = practice.status === "not_approved";
  const isActionBlocked = isNotApproved;
  const supportCaption = `Contact ${appConfig.support.email} for more information.`;
  const status = practice.isPrimary
    ? { label: "Primary", className: "status-pill primary", title: "Primary practice location.", icon: <Home size={14} aria-hidden="true" /> }
    : isPending
      ? { label: "Pending approval", className: "status-pill pending", title: supportCaption, icon: null }
      : isNotApproved
        ? { label: "Not approved", className: "status-pill not-approved", title: supportCaption, icon: null }
        : { label: "Active", className: "status-pill active", title: "Approved practice location.", icon: null };

  return (
    <article className={isActionBlocked ? "practice-row muted" : "practice-row"}>
      <div>
        <div className="practice-title-row">
          <h3>{practice.name}</h3>
          <span className={status.className} title={status.title}>
            {status.icon}
            {status.label}
          </span>
        </div>
        <p>{practice.address}</p>
      </div>
      <div className="practice-actions">
        {!practice.isPrimary && !isPending && !isActionBlocked ? (
          <button
            className="secondary-button compact"
            type="button"
            disabled={isActionDisabled}
            aria-busy={busyAction === "primary"}
            onClick={onMakePrimary}
          >
            {busyAction === "primary" ? "Setting primary..." : "Make primary"}
          </button>
        ) : null}
        <button
          className="icon-button danger"
          type="button"
          aria-label={`Delete ${practice.name}`}
          disabled={isActionBlocked || practice.isPrimary || isActionDisabled}
          aria-busy={busyAction === "delete"}
          onClick={onDelete}
        >
          {busyAction === "delete" ? <LoaderCircle size={16} className="spin" /> : <Trash2 size={16} />}
        </button>
      </div>
    </article>
  );
}

function SearchableSelect({
  options,
  placeholder,
  value,
  valueLabel,
  onChange
}: {
  options: Array<{ value: string; label: string; searchText?: string }>;
  placeholder: string;
  value: string;
  valueLabel: string;
  onChange: (value: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [opensUp, setOpensUp] = useState(false);
  const controlRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions = useMemo(
    () =>
      options
        .filter((option) => {
          if (!normalizedQuery) return true;
          return `${option.label} ${option.searchText ?? ""}`.toLowerCase().includes(normalizedQuery);
        })
        .slice(0, 80),
    [normalizedQuery, options]
  );

  function openMenu() {
    const rect = controlRef.current?.getBoundingClientRect();
    if (rect) {
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpensUp(spaceBelow < 220 && spaceAbove > spaceBelow);
    }
    setIsOpen(true);
  }

  function selectOption(optionValue: string) {
    setQuery("");
    setIsOpen(false);
    onChange(optionValue);
    window.requestAnimationFrame(() => {
      setIsOpen(false);
      inputRef.current?.blur();
    });
  }

  return (
    <div className={opensUp ? "searchable-select open-up" : "searchable-select"}>
      <div className="searchable-select-control" ref={controlRef}>
        <input
          ref={inputRef}
          aria-autocomplete="list"
          aria-expanded={isOpen}
          autoComplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          placeholder={placeholder}
          value={isOpen ? query : valueLabel}
          onBlur={() => window.setTimeout(() => setIsOpen(false), 120)}
          onChange={(event) => {
            setQuery(event.target.value);
            openMenu();
          }}
          onFocus={() => {
            setQuery("");
            openMenu();
          }}
          required={!value}
        />
        <ChevronDown size={18} aria-hidden="true" />
      </div>
      {isOpen ? (
        <div className="searchable-select-menu" role="listbox">
          {visibleOptions.length ? (
            visibleOptions.map((option) => (
              <button
                className={option.value === value ? "searchable-select-option selected" : "searchable-select-option"}
                type="button"
                role="option"
                aria-selected={option.value === value}
                key={option.value}
                onPointerDown={(event) => {
                  event.preventDefault();
                  selectOption(option.value);
                }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.preventDefault();
                  if (event.detail === 0) {
                    selectOption(option.value);
                  }
                }}
              >
                {option.label}
              </button>
            ))
          ) : (
            <p className="searchable-select-empty">No matches found.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
